'use client';

import React, { useEffect, useState } from 'react';
import { createPortal } from 'react-dom';
import { X } from 'lucide-react';

interface ModalProps {
  open: boolean;
  onClose: () => void;
  title: string;
  children: React.ReactNode;
  size?: 'sm' | 'md' | 'lg';
}

export default function Modal({ open, onClose, title, children, size = 'md' }: ModalProps) {
  const [mounted, setMounted] = useState(false);

  useEffect(() => {
    setMounted(true);
  }, []);

  useEffect(() => {
    if (!mounted) return;
    if (open) {
      document.body.style.overflow = 'hidden';
    } else {
      document.body.style.overflow = '';
    }
    return () => { document.body.style.overflow = ''; };
  }, [mounted, open]);

  if (!mounted || !open) return null;

  const sizeClass = size === 'sm' ? 'max-w-sm' : size === 'lg' ? 'max-w-2xl' : 'max-w-lg';

  return createPortal(
    <div
      className="fixed inset-0 z-50 flex items-center justify-center p-2 animate-fade-in sm:p-4"
      role="dialog"
      aria-modal="true"
      aria-labelledby="modal-title"
    >
      <div className="absolute inset-0 bg-[#010611]/75 backdrop-blur-md" onClick={onClose} />
      <div className={`app-outer-panel relative w-full ${sizeClass} max-h-[88vh] overflow-hidden border bg-[linear-gradient(145deg,rgba(3,17,38,.98),rgba(2,12,27,.98))] shadow-2xl shadow-black/50 animate-slide-up`}>
        <div className="flex items-center justify-between border-b border-[#0D4166] bg-cyan-400/[0.035] p-5">
          <h2 id="modal-title" className="text-base font-bold text-foreground">{title}</h2>
          <button
            onClick={onClose}
            className="p-1.5 rounded-lg hover:bg-muted/60 transition-colors duration-150 text-muted-foreground hover:text-foreground"
            aria-label="Close modal"
          >
            <X size={16} />
          </button>
        </div>
        <div className="max-h-[calc(88vh-65px)] overflow-y-auto p-3 sm:p-5">{children}</div>
      </div>
    </div>,
    document.body,
  );
}
